import { useEffect, useState, type RefObject } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { slugifyHeading } from "@/lib/portal/doc-slug";
import {
  DOC_SECTION_SEARCH_PARAM,
  getDocPageHashHref,
  scrollToDocAnchor,
} from "@/lib/portal/doc-anchor";

export interface DocTocItem {
  id: string;
  label: string;
  level: 1 | 2 | 3;
}

function headingLevel(tagName: string): 1 | 2 | 3 {
  if (tagName === "H1") return 1;
  if (tagName === "H3") return 3;
  return 2;
}

function ensureHeadingIds(container: HTMLElement): DocTocItem[] {
  const items: DocTocItem[] = [];
  const used = new Set<string>();

  container.querySelectorAll("h1, h2, h3").forEach((node, index) => {
    const el = node as HTMLHeadingElement;
    const level = headingLevel(el.tagName);
    const label = el.textContent?.trim() ?? "";
    if (!label) return;

    const base = slugifyHeading(label) || `section-${index}`;
    let id = el.id || base;

    if (!el.id) {
      let candidate = id;
      let suffix = 2;
      while (used.has(candidate)) {
        candidate = `${base}-${suffix}`;
        suffix += 1;
      }
      id = candidate;
      el.id = id;
    }

    used.add(id);
    items.push({ id, label, level });
  });

  return items;
}

function DocTocAnchor({ id, children }: { id: string; children: string }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <a
      href={getDocPageHashHref(pathname, id)}
      onClick={(event) => {
        event.preventDefault();
        navigate(
          { pathname, search: `?${DOC_SECTION_SEARCH_PARAM}=${encodeURIComponent(id)}` },
          { replace: true },
        );
        scrollToDocAnchor(id);
      }}
    >
      {children}
    </a>
  );
}

interface DocAutoTocProps {
  /** Minimum headings required to show the TOC (default 2). */
  minItems?: number;
  /** When true, the TOC is not rendered. */
  hidden?: boolean;
  /** Root element to scan (defaults to `.doc-layout`, then `.doc-markdown-body`). */
  rootRef?: RefObject<HTMLElement | null>;
  /** Re-scan when doc content changes (e.g. route id). */
  contentKey?: string;
}

export function DocAutoToc({
  minItems = 2,
  hidden = false,
  rootRef,
  contentKey,
}: DocAutoTocProps) {
  const [items, setItems] = useState<DocTocItem[]>([]);

  useEffect(() => {
    if (hidden) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      const container =
        rootRef?.current ??
        document.querySelector<HTMLElement>(".doc-layout") ??
        document.querySelector<HTMLElement>(".doc-markdown-body");
      if (!(container instanceof HTMLElement)) {
        return;
      }
      setItems(ensureHeadingIds(container));
    });
    return () => cancelAnimationFrame(frame);
  }, [hidden, contentKey, rootRef]);

  if (hidden || items.length < minItems) {
    return null;
  }

  return (
    <nav className="toc" aria-label="On this page">
      <div className="toc-t">On this page</div>
      <ul className="toc-list">
        {items.map((item) => (
          <li key={item.id} className={item.level === 3 ? "toc-item toc-item-nested" : "toc-item"}>
            <DocTocAnchor id={item.id}>{item.label}</DocTocAnchor>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Manual TOC when headings are known ahead of time (optional). */
export function DocToc({ items }: { items: Omit<DocTocItem, "level">[] }) {
  if (items.length === 0) return null;

  return (
    <nav className="toc" aria-label="On this page">
      <div className="toc-t">On this page</div>
      <ul className="toc-list">
        {items.map((item) => (
          <li key={item.id} className="toc-item">
            <DocTocAnchor id={item.id}>{item.label}</DocTocAnchor>
          </li>
        ))}
      </ul>
    </nav>
  );
}
